import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatINR, formatDate } from "../lib/api";
import { ArrowLeft, User } from "lucide-react";

function Stat({ label, value, testId }) {
  return (
    <div className="bg-white border border-[#E5E3DB] p-4">
      <div className="text-[11px] uppercase tracking-wide text-[#68665E]">{label}</div>
      <div className="text-lg font-semibold text-[#1A1A18] mt-1" data-testid={testId}>{value}</div>
    </div>
  );
}

export default function CustomerWork() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/customers/${encodeURIComponent(name)}/work`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [name]);

  if (!data) return <div className="text-sm text-[#68665E]">Loading…</div>;
  if (data.error) return <div className="text-sm text-[#8A2526]">Could not load customer.</div>;
  const s = data.summary;

  return (
    <div data-testid="customer-work-page">
      <button className="btn-ghost text-sm mb-4" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-center gap-3 mb-6">
        <User className="w-8 h-8 text-[#C84B31]" />
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Customer / Party</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18]" data-testid="customer-work-name">{decodeURIComponent(name)}</h1>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <Stat label="LRs" value={s.lr_count} testId="cw-lr-count" />
        <Stat label="Total Freight" value={formatINR(s.total_freight)} testId="cw-freight" />
        <Stat label="Invoiced" value={formatINR(s.total_invoiced)} testId="cw-invoiced" />
        <Stat label="Paid" value={formatINR(s.paid)} />
        <Stat label="Outstanding" value={formatINR(s.outstanding)} testId="cw-outstanding" />
      </div>

      <h2 className="font-headings text-lg font-bold mb-3">Lorry Receipts</h2>
      <div className="table-wrap mb-8">
        <table className="w-full">
          <thead><tr><th className="table-th">LR #</th><th className="table-th">Date</th><th className="table-th">Route</th><th className="table-th">Transit</th><th className="table-th">Freight</th><th className="table-th">Status</th></tr></thead>
          <tbody>
            {data.lrs.length === 0 ? <tr><td className="table-td text-center text-[#68665E]" colSpan={6}>No LRs.</td></tr> : data.lrs.map((lr) => (
              <tr key={lr.id} className="table-row" data-testid={`cw-lr-${lr.id}`}>
                <td className="table-td"><button className="text-[#C84B31] hover:underline font-mono text-xs" onClick={()=>navigate(`/lr/${lr.id}`)}>{lr.lr_number}</button></td>
                <td className="table-td">{formatDate(lr.lr_date)}</td>
                <td className="table-td">{lr.from_location} → {lr.to_location}</td>
                <td className="table-td">{lr.transit_days == null ? "—" : `${lr.transit_days}d`}</td>
                <td className="table-td font-medium">{formatINR(lr.freight_amount)}</td>
                <td className="table-td"><span className={lr.status === "delivered" ? "badge-success" : lr.status === "cancelled" ? "badge-error" : "badge-pending"}>{String(lr.status).replace("_"," ")}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-headings text-lg font-bold mb-3">Invoices</h2>
      <div className="table-wrap">
        <table className="w-full">
          <thead><tr><th className="table-th">Invoice #</th><th className="table-th">Date</th><th className="table-th">Total</th><th className="table-th">Status</th></tr></thead>
          <tbody>
            {data.invoices.length === 0 ? <tr><td className="table-td text-center text-[#68665E]" colSpan={4}>No invoices.</td></tr> : data.invoices.map((i) => (
              <tr key={i.id} className="table-row" data-testid={`cw-inv-${i.id}`}>
                <td className="table-td font-mono text-xs">{i.invoice_number}</td>
                <td className="table-td">{formatDate(i.invoice_date)}</td>
                <td className="table-td font-medium">{formatINR(i.total)}</td>
                <td className="table-td"><span className={i.status === "paid" ? "badge-success" : i.status === "overdue" ? "badge-error" : "badge-pending"}>{i.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
